import { AuthServiceImpl } from "@/services/auth/AuthServiceImpl";
import { UserServiceImpl } from "@/services/user/UserServiceImpl";
import { SixDetailServiceImpl } from "@/services/sixDetail/SixDetailServiceImpl";
import { CreateSixServiceImpl } from "@/services/createSix/CreateSixServiceImpl";
import { SixCatalogServiceImpl } from "@/services/sixCatalog/SixCatalogServiceImpl";
import { OrganizationServiceImpl } from "@/services/organization/OrganizationServiceImpl";
import { HttpFetcherService } from "@/services/http/HttpFetcherService";
import type { AppServices } from "@/types/shared/services.types";

import { Libp2pServiceImpl } from "./poc/libp2p.service.impl";
import { HeliaServiceImpl } from "./poc/helia.service.impl";
import type { HeliaService } from "./abstract/poc/helia.service.interface";
import type { Libp2pService } from "./abstract/poc/libp2p.service.interface";
import { createFileUploadedHandler } from "./poc/handlers/fileAnnouncementHandler";
import { createFilesUpdatedHandler } from "./poc/handlers/fileListHandler";
import { createFileClonedHandler } from "./poc/handlers/fileClonedHandler";
import { createFileUploadedAcknowledgedHandler } from "./poc/handlers/fileUploadedAcknowledgedHandler";
import { createFileKeyReadyForDownloadAcknowledgedHandler } from "./poc/handlers/fileKeyReadyForDownloadAcknowledgedHandler";
import { createCheckForKeysNeedingRegeneratingHandler } from "./poc/handlers/checkForKeysNeedingRegeneratingHandler";
import { createFileReplicationStartedHandler } from "./poc/handlers/fileReplicationStartedHandler";
import { createFileReplicationProgressHandler } from "./poc/handlers/fileReplicationProgressHandler";
import { createFileReplicationFailedHandler } from "./poc/handlers/fileReplicationFailedHandler";
import type { MessagingService } from "./abstract/poc/messaging.service.interface";
import { MessagingServiceImpl } from "./poc/messaging.service.impl";
import { CryptoServiceImpl } from "./poc/crypto.service.impl";
import type { PocAuthService } from "./abstract/poc/pocAuth.service.interface";
import { PocAuthServiceImpl } from "./poc/pocAuthService";
import type { CryptoService } from "./abstract/poc/crypto.service.interface";
import { P2PStackManagerImpl } from "./poc/p2p.stack-manager.impl";
import type { P2PStackManager } from "./abstract/poc/p2p.stack-manager.interface";
import { NetworkFileServiceImpl } from "./poc/networkfile.service.impl";
import { FileEncryptionServiceImpl } from "./poc/fileEncryption.service.impl";
import { LoggerServiceImpl } from "./logger/LoggerServiceImpl";
import { CidDownloadLoggerImpl } from "./poc/cidDownloadLogger.service.impl";
import { FileKeyFacilitationServiceImpl } from "./fileKey/FileKeyFacilitationServiceImpl";

export const createJsonServices= (): AppServices => {
  const httpService = new HttpFetcherService();
  const loggerService = new LoggerServiceImpl();

  const authService     = new AuthServiceImpl(httpService);
  const userService     = new UserServiceImpl(httpService);
  const sixDetailService = new SixDetailServiceImpl(httpService);
  const createSixService = new CreateSixServiceImpl(httpService);
  const sixCatalogService = new SixCatalogServiceImpl(httpService);
  const organizationService = new OrganizationServiceImpl(httpService);
  const fileKeyFacilitationService = new FileKeyFacilitationServiceImpl(httpService);
  const libp2pService: Libp2pService = new Libp2pServiceImpl(loggerService);
  const heliaService:HeliaService = new HeliaServiceImpl(loggerService);

  // Create message handlers (pure functions)
  const messageHandlers = [
    createFileUploadedHandler(),
    createFilesUpdatedHandler(),
    createFileClonedHandler(),
    createFileUploadedAcknowledgedHandler(),
    createFileKeyReadyForDownloadAcknowledgedHandler(),
    createCheckForKeysNeedingRegeneratingHandler(),
    createFileReplicationStartedHandler(),
    createFileReplicationProgressHandler(),
    createFileReplicationFailedHandler()
  ]
  // Messaging service with injected dependencies
  const messagingService: MessagingService = new MessagingServiceImpl(
    loggerService,
    libp2pService,
    messageHandlers,
    'six1'
  );
  const cryptoService: CryptoService = new CryptoServiceImpl();
  const fileEncryptionService = new FileEncryptionServiceImpl(cryptoService);
  const pocAuthService: PocAuthService = new PocAuthServiceImpl(cryptoService, httpService);

  // P2P Stack service depends on libp2pService
  const p2pStackManagerService: P2PStackManager = new P2PStackManagerImpl(loggerService, libp2pService, heliaService, messagingService);
  const networkFileService = new NetworkFileServiceImpl();
  const cidDownloadLogger = new CidDownloadLoggerImpl();

  const pocServices = {
    libp2pService,
    heliaService,
    p2pStackManagerService,
    messagingService,
    pocAuthService,
    cryptoService,
    networkFileService,
    fileEncryptionService,
    cidDownloadLogger,
  };

  return { authService, userService, sixDetailService, createSixService, sixCatalogService, organizationService, loggerService, fileKeyFacilitationService, pocServices };
};
